import { HttpClient, HttpContext, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { of, firstValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import { ApiResult } from "../models/api-result.model";
import { ArtistModel } from "../models/artist.model";
import { BrandModel } from "../models/brand.model";
import { CategoryModel } from "../models/category.model";
import { ContentLogAction } from "../models/content-log-action.enum";
import { ContentModel } from "../models/content.model";
import { ContentPlaybackModel } from "../models/content-playback.model";
import { LanguageModel } from "../models/language.model";
import { UserContentProgressModel } from "../models/user-content-progress.model";
import { PlaylistModel } from "../models/playlist.model";
import { UserModel } from "../models/user.model";
import { PlanModel, SubscriptionStatus } from "../models/plan.model";
import { UpgradePlanResponseModel } from "../models/upgrade-plan-response.model";
import { AppConfigurationModel } from "../models/app-configuration.model";
import { checkStatus } from "../utils/check-status";
import { toQueryString } from "../utils/to-query-string";

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(private http: HttpClient) {
  }

  private httpGet<T>(path: string, options?: {
    headers?: HttpHeaders | {
      [header: string]: string | string[];
    };
    context?: HttpContext;
    observe?: 'body';
    params?: HttpParams | {
      [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
    transferCache?: {
      includeHeaders?: string[];
    } | boolean
  }) {
    return this.http.get<ApiResult<T>>(environment.baseUrl + path, options)
      .pipe(checkStatus());
  }

  private httpGetAsync<T>(path: string) {
    return firstValueFrom(this.httpGet<T>(path));
  }
  private httpPost<T>(path: string, request: any) {
    return this.http.post<ApiResult<T>>(environment.baseUrl + path, request)
      .pipe(checkStatus());
  }

  private httpPostAsync<T>(path: string, request: any) {
    return firstValueFrom(this.httpPost<T>(path, request));
  }

  public getBrand() {
    return this.httpGetAsync<BrandModel>('/api/v1/brands/current');
  }

  public getAppConfiguration() {
    return this.httpGetAsync<AppConfigurationModel>('/api/v1/app-config/current');
  }

  public currentUser() {
    return this.httpGetAsync<UserModel>('/api/v1/users/current');
  }

  public authenticate(code: string) {
    return this.httpPostAsync<{ token: string }>('/api/v1/users/authenticate', { code });
  }

  public getCategories() {
    return this.httpGet<CategoryModel[]>('/api/v1/categories');
  }

  public getContents(categoryId: number) {
    return this.httpGet<ContentModel[]>(`/api/v1/contents/${categoryId}`);
  }

  public getFavourites() {
    return this.httpGet<ContentModel[]>(`/api/v1/contents/favourites`);
  }

  public getPopular() {
    return this.httpGet<ContentModel[]>(`/api/v1/contents/popular`);
  }

  public getContent(contentId: number) {
    return this.httpGet<ContentModel>(`/api/v1/contents/content?contentId=${contentId}`);
  }

  public playContent(contentId: number) {
    return this.httpPostAsync<ContentPlaybackModel>(`/api/v1/contents/play/${contentId}`, {});
  }

  public getArtist(artistId?: number) {
    return this.httpGet<ArtistModel>(`/api/v1/artists/get?artistId=${artistId}`);
  }

  public getPlaylist(playlistId: number) {
    return this.httpGet<PlaylistModel>(`/api/v1/playlists/get?playlistid=${playlistId}`);
  }

  public getContentList(search: { contentId?: number, playlistId?: number, artistId?: number }) {
    return this.httpGet<ContentModel[]>(`/api/v1/contents/contentList?${toQueryString(search)}`);
  }

  public search(query: { title?: string, artist?: string, playlist?: string }, page: number) {
    return this.httpGet<ContentModel[]>(`/api/v1/contents/search`, {
      params: {
        ...query,
        page: page.toString()
      }
    });
  }

  public getArtists(searchText: string) {
    return this.httpGet<ArtistModel[]>(`/api/v1/artists/list?searchText=${searchText}`);
  }

  public getPlaylists() {
    return this.httpGet<PlaylistModel[]>(`/api/v1/playlists/list`);
  }

  public savePlaylist(playlist: PlaylistModel) {
    return this.httpPostAsync<PlaylistModel>(`/api/v1/playlists/save`, playlist);
  }

  public searchPlaylist(searchText?: string) {
    return this.httpGet<ContentModel[]>(`/api/v1/playlists/search?searchText=${searchText}`);
  }

  public deletePlaylist(playlist: PlaylistModel) {
    return this.httpPostAsync<PlaylistModel>(`/api/v1/playlists/delete`, playlist);
  }

  public addSongToPlaylists(contentId: number, playlistIds: number[]) {
    return this.httpPostAsync(`/api/v1/contents/saveToPlaylists`, { contentId, playlistIds });
  }

  public removeSongsFromPlaylist(playlistId: number, contentIds: number[]) {
    return this.httpPostAsync('/api/v1/playlists/removeSongs', { contentIds, playlistId });
  }

  public getTranslationData(language: string) {
    return this.httpGetAsync<object>(`/api/v1/translations/data/${language}`);
  }

  public getLanguages() {
    return this.httpGet<LanguageModel[]>(`/api/v1/translations/languages`);
  }

  public logContent(contentId: number, action: ContentLogAction) {
    return this.httpPostAsync('/api/v1/contents/log', { contentId, action });
  }

  // ── Content progress ─────────────────────────────────────────────────────

  public getContentProgress(contentId: number) {
    return this.httpGetAsync<UserContentProgressModel>(`/api/v1/progress?contentId=${contentId}`);
  }

  public saveContentProgress(contentId: number, current: number, total: number, lastViewed: Date, completed: boolean) {
    return this.httpPostAsync<void>('/api/v1/progress', { contentId, current, total, lastViewed, completed });
  }
  
  // ── Plans ──────────────────────────────────────────────────────────────

  public canPlaySong() {
    return this.httpGetAsync<boolean>('/api/v1/plans/canplay');
  }

  public getCurrentPlan() {
    return this.httpGetAsync<PlanModel>('/api/v1/plans/current');
  }

  public syncUserPlan() {
    return this.httpPostAsync<UserModel>('/api/v1/plans/current/sync', {});
  }

  public getUpgradePlans() {
    return this.httpGetAsync<PlanModel[]>('/api/v1/plans/upgrades');
  }

  public upgradePlan(planId: number) {
    return this.httpPostAsync<UpgradePlanResponseModel>('/api/v1/plans/upgrade', { planId });
  }

  async updateStatus(status: SubscriptionStatus, reason: string) {
    return await this.httpPostAsync<void>('/api/v1/plans/updatestatus', { status, reason });
  }

  processUpgrade(key: string) {
    return this.httpPost<SubscriptionStatus | null>('/api/v1/plans/process-upgrade', { key });
  }
}
